import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { gmailCompose, whatsAppLink } from '@/features/communications/queries'
import { questionnaireMessage } from '@/lib/questionnaire'
import { questionnaireLink, useUpdateQuestionnaire, type SentQuestionnaire } from './queries'

/**
 * Gmail, WhatsApp or Copy: opens the message with the couple's link for Jena
 * to send herself, and marks the questionnaire sent. Nothing is sent from the
 * Studio.
 */
export function SendQuestionnaire({ questionnaire }: { questionnaire: SentQuestionnaire }) {
  const update = useUpdateQuestionnaire()
  const job = questionnaire.job
  // On a project the message goes to the couple; otherwise to the client.
  const people = (job ? [job.primary_contact, job.partner_contact] : [questionnaire.contact]).filter(
    (person) => person !== null && person !== undefined,
  )
  const emails = people.map((person) => person.email).filter((email): email is string => Boolean(email))
  const phone = people.find((person) => person.phone)?.phone ?? null
  const message = questionnaireMessage(
    people.map((person) => person.first_name).join(' & '),
    questionnaire.name,
    questionnaireLink(questionnaire.token),
  )

  function markSent() {
    if (!questionnaire.sent_at) update.mutate({ id: questionnaire.id, sent_at: new Date().toISOString() })
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[length:var(--text-2xs)] text-taupe">{questionnaire.sent_at ? 'Send again:' : 'Send it:'}</span>
      {emails.length ? (
        <Button
          size="sm"
          variant={questionnaire.sent_at ? 'ghost' : 'primary'}
          onClick={() => {
            window.open(gmailCompose(emails.join(','), questionnaire.name, message), '_blank', 'noopener')
            markSent()
          }}
        >
          Open in Gmail
        </Button>
      ) : null}
      {whatsAppLink(phone, message) ? (
        <Button
          size="sm"
          variant={questionnaire.sent_at || emails.length ? 'ghost' : 'primary'}
          onClick={() => {
            window.open(whatsAppLink(phone, message) ?? '', '_blank', 'noopener')
            markSent()
          }}
        >
          Open in WhatsApp
        </Button>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          void navigator.clipboard.writeText(message).then(() => {
            toast.success('Message copied', { description: 'Paste it to them. It now shows as sent.' })
            markSent()
          })
        }}
      >
        Copy message
      </Button>
    </div>
  )
}
